import React, { useState } from "react";
import {
  Building,
  Car,
  Utensils,
  Server,
  ShieldAlert,
  MapPin,
  Users,
  AlertTriangle,
} from "lucide-react";

const Label = ({ children, className = "" }) => (
  <span
    className={`text-[10px] font-bold uppercase tracking-[0.8px] text-slate-500 ${className}`}
  >
    {children}
  </span>
);

const gridData = [
  {
    id: "b10",
    name: "B10",
    icon: Building,
    people: 2280,
    occupancy: 91,
    tag: "Server L3 · Reception",
    restricted: true,
    highlight: true,
    badges: [
      { severity: "critical", count: 2 },
      { severity: "high", count: 3 },
      { severity: "medium", count: 5 },
    ],
    zones: [
      "GF Reception",
      "GF Lobby",
      "Server L3",
      "Loading Dock",
      "Parking P1·P2",
    ],
    alert: "Watch lobby at 18:00 exit",
  },
  {
    id: "b20",
    name: "B20",
    icon: Building,
    people: 1510,
    occupancy: 63,
    tag: "Loading dock · Reception",
    restricted: false,
    highlight: false,
    badges: [
      { severity: "critical", count: 1 },
      { severity: "high", count: 2 },
      { severity: "medium", count: 4 },
    ],
    zones: ["GF Reception", "GF Lobby", "Loading Dock", "Server Room"],
    alert: null,
  },
  {
    id: "b30",
    name: "B30",
    icon: Building,
    people: 1412,
    occupancy: 58,
    tag: "Cafeteria · Reception",
    restricted: false,
    highlight: false,
    badges: [
      { severity: "critical", count: 1 },
      { severity: "high", count: 1 },
      { severity: "medium", count: 3 },
    ],
    zones: ["GF Reception", "Cafeteria", "Loading Dock", "Server Room"],
    alert: null,
  },
  {
    id: "mlcp",
    name: "MLCP",
    icon: Building,
    people: 960,
    occupancy: 43,
    tag: "Parking · Stairwells",
    restricted: false,
    highlight: false,
    badges: [
      { severity: "high", count: 1 },
      { severity: "medium", count: 2 },
    ],
    zones: ["GF Lobby", "Parking P1·P2", "Stairwells"],
    alert: null,
  },
  {
    id: "outside",
    name: "OUTSIDE",
    icon: Users,
    people: 320,
    occupancy: null,
    tag: "Main Gate · Gate 2",
    restricted: false,
    highlight: false,
    badges: [
      { severity: "critical", count: 2 },
      { severity: "high", count: 1 },
    ],
    zones: ["Main Gate", "Gate 2", "Driveways"],
    alert: "Gate 2 loitering - tune threshold",
  },
  {
    id: "parking",
    name: "Parking P1 · P2",
    icon: Car,
    people: null,
    vehicles: 1140,
    vehiclePercent: 79,
    tag: "MLCP · B10 · B20",
    restricted: false,
    highlight: false,
    badges: [
      { severity: "medium", count: 4 },
      { severity: "high", count: 1 },
    ],
    zones: ["MLCP Parking", "B10 Parking", "B20 Parking"],
    alert: null,
  },
  {
    id: "foodcourt",
    name: "Food court & amenities",
    icon: Utensils,
    people: 610,
    occupancy: null,
    tag: "B30 · B20",
    restricted: false,
    highlight: false,
    badges: [{ severity: "medium", count: 2 }],
    zones: ["B30 Cafeteria", "B20 Cafeteria"],
    alert: null,
  },
  {
    id: "utilities",
    name: "Utilities · BMS",
    icon: Server,
    people: null,
    tag: "B10 · B20 · B30",
    restricted: true,
    highlight: false,
    badges: [
      { severity: "critical", count: 8 },
      { severity: "high", count: 3 },
    ],
    zones: [
      "B10 Server L3",
      "B20 Server Room",
      "B30 Server Room",
      "BMS",
    ],
    alert: "Server rooms - restricted access",
  },
];

const severityDot = {
  critical: "bg-red-500",
  high: "bg-amber-500",
  medium: "bg-blue-500",
};

const severityLabels = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
};

const CampusLiveView = ({ onZoneClick }) => {
  const [selected, setSelected] = useState(null);
  const [expandedTile, setExpandedTile] = useState(null);

  const handleSelect = (tile) => {
    if (!tile || !tile.id) return;

    setSelected(tile.id);
    setExpandedTile(expandedTile === tile.id ? null : tile.id);

    if (typeof onZoneClick === "function") {
      onZoneClick(tile);
    }
  };

  const getTileBadgeCount = (tile) => {
    if (!tile?.badges) return 0;
    return tile.badges.reduce((sum, badge) => sum + (badge.count || 0), 0);
  };

  const getTileById = (id) => gridData.find((tile) => tile.id === id);

  const selectedTile = getTileById(selected);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {gridData.map((tile) => {
          const Icon = tile.icon || Building;
          const isSelected = selected === tile.id;
          const isExpanded = expandedTile === tile.id;
          const totalBadges = getTileBadgeCount(tile);

          return (
            <button
              key={tile.id}
              type="button"
              onClick={() => handleSelect(tile)}
              className={`relative rounded-xl border p-3 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
                tile.restricted
                  ? "border-dashed border-violet-300"
                  : "border-solid border-slate-200"
              } ${
                tile.highlight
                  ? "bg-amber-50 shadow-sm"
                  : isSelected
                    ? "border-violet-300 bg-violet-50 shadow-sm"
                    : "bg-white hover:bg-slate-50 hover:shadow-sm"
              }`}
            >
              {totalBadges > 0 && (
                <div className="absolute -right-1 -top-1 flex items-center gap-0.5">
                  {tile.badges.map((badge, index) => (
                    <span
                      key={`${tile.id}-${badge.severity}-${index}`}
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold text-white shadow-sm ${
                        severityDot[badge.severity] || "bg-slate-500"
                      }`}
                      title={`${severityLabels[badge.severity] || "Alert"}: ${badge.count}`}
                    >
                      {badge.count}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1.5 pr-8">
                <Icon size={14} className="shrink-0 text-slate-400" />
                <span className="truncate text-xs font-bold text-slate-900">
                  {tile.name || "Unknown"}
                </span>
              </div>

              {tile.people != null && (
                <p className="mt-1 text-[10px] text-slate-500">
                  {tile.people.toLocaleString()}
                  {tile.occupancy != null ? ` · ${tile.occupancy}% occupied` : ""}
                </p>
              )}

              {tile.vehicles != null && (
                <p className="mt-1 text-[10px] text-slate-500">
                  {tile.vehicles.toLocaleString()} vehicles ·{" "}
                  {tile.vehiclePercent || 0}%
                </p>
              )}

              {tile.tag && (
                <p className="mt-2 truncate text-[10px] text-slate-400">
                  {tile.tag}
                </p>
              )}

              {tile.restricted && (
                <span className="mt-1 inline-flex items-center gap-1 text-[9px] font-medium text-violet-600">
                  <ShieldAlert size={10} />
                  Restricted
                </span>
              )}

              {tile.alert && (
                <div className="mt-2 flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-1 text-[9px] text-amber-700">
                  <AlertTriangle size={10} />
                  <span className="truncate">{tile.alert}</span>
                </div>
              )}

              {tile.zones?.length > 0 && (
                <div className="mt-2 text-[9px] font-medium text-slate-400">
                  {isExpanded ? "▼" : "▶"} {tile.zones.length} zones
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-[10px] text-slate-500">Critical</span>
          </span>

          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            <span className="text-[10px] text-slate-500">High</span>
          </span>

          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            <span className="text-[10px] text-slate-500">Medium</span>
          </span>

          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm border border-dashed border-violet-400" />
            <span className="text-[10px] text-slate-500">
              Restricted zone
            </span>
          </span>
        </div>

        <span className="text-[10px] text-slate-400">
          Select a location to view zone details
        </span>
      </div>

      {selectedTile && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <MapPin size={15} className="text-violet-600" />
              <span className="text-xs font-bold text-slate-900">
                {selectedTile.name || "Unknown"}
              </span>
              <span className="text-[10px] text-slate-400">
                {selectedTile.tag || ""}
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setExpandedTile(null);
              }}
              className="text-[10px] font-medium text-slate-400 transition-colors hover:text-slate-700"
            >
              Close
            </button>
          </div>

          {expandedTile && (
            <div className="mt-3 border-t border-slate-200 pt-3">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {selectedTile.zones?.map((zone, index) => (
                  <div
                    key={`${selectedTile.id}-zone-${index}`}
                    className="flex items-center gap-1.5 text-[10px] text-slate-600"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-violet-300" />
                    {zone}
                  </div>
                ))}
              </div>

              {selectedTile.badges?.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] text-slate-400">Alerts:</span>

                  {selectedTile.badges.map((badge, index) => (
                    <span
                      key={`${selectedTile.id}-badge-${index}`}
                      className={`rounded-full px-2 py-0.5 text-[9px] font-bold text-white ${
                        severityDot[badge.severity] || "bg-slate-500"
                      }`}
                    >
                      {severityLabels[badge.severity] || "Alert"}:{" "}
                      {badge.count}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CampusLiveView;